import React, { useEffect, useState } from "react";
import { Container, Div, Text } from "../../styles/Common";
import { theme } from "../../styles/theme";
import { useNavigate, useParams } from "react-router-dom";
import back from "../../assets/icons/arrow-left.svg";
import searchIcon from "../../assets/icons/search.svg";
import filterIcon from "../../assets/icons/filter.svg";
import closeIcon from "../../assets/icons/close.svg";
import { Input } from "../../components/Input";
import { Button } from "../../components/Button";
import { WrapperStatus } from "../UserManagement/styles";
import { FiltersTable } from "../UserManagement/components/FiltersTable";
import { Link } from "../../components/Link";
import { Table } from "../../components/Table";
import { Skeleton } from "../../components/Skeleton";
import { AddUserToProjectModal } from "../Projects/components/AddUserToProjectModal";
import { ModalConfirm } from "./components/ModalConfirm";
import useGetUsersByProject from "./hooks/useGetUsersByProject";
import useDebounce from "../../core/hooks/useDebounce";
import useGetRoles from "../CreateProject/hooks/useGetRoles";
import useGetActiveUsers from "../CreateProject/hooks/useGetActiveUsers";
import useRevokeAccess from "../UserDetail/hooks/useRevokeAccess";
import { hasPermission } from "../../utilities/permissions";

export const UsersByProject = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const revokeAccess = useRevokeAccess();
  const [dataUsers, setDataUsers] = useState([]);
  const [totalItems, setTotalItems] = useState(10);
  const [pages, setPages] = useState({ current: 1, pageSize: 10 });
  const [searchBy, setSearchBy] = useState("");
  const searchValue = useDebounce(searchBy, 600);
  const [status, setStatus] = useState("");
  const [usersInfo, setUsersInfo] = useState([
    {
      id: 1,
      userId: "",
      role: "",
    }
  ]);
  const getUsers = useGetUsersByProject(
    pages.current,
    pages.pageSize,
    searchValue,
    status,
    projectId,
  );
  const { data, isSuccess, isLoading, refetch } = getUsers;
  const getRoles = useGetRoles();
  const { data: dataRoles, isSuccess: isRolesSuccess } = getRoles;
  const [rolesOptions, setRolesOptions] = useState([]);
  const getActiveUsers = useGetActiveUsers();
  const { data: activeUsers, isSuccess: isActiveUsersSuccess } = getActiveUsers;
  const [activeUsersOptions, setActiveUsersOptions] = useState([]);
  const [userRolesProject, setUserRolesProject] = useState([]);
  const [forceUpdate, setForceUpdate] = useState(false);
  const [existingUsers, setExistingUsers] = useState([]);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);

  const getBackgroundStatus = (status) => {
    switch (status) {
      case "Active":
        return theme.colors.greenLight;
      case "Inactive":
        return theme.colors.salmon1;
      case "Unconfirmed":
        return theme.colors.yellowLight;
      default:
        return theme.colors.grey3;
    }
  };

  const getColorStatus = (status) => {
    switch (status) {
      case "Active":
        return theme.colors.green;
      case "Inactive":
        return theme.colors.salmon;
      case "Unconfirmed":
        return theme.colors.yellow;
      default:
        return theme.colors.grey1;
    }
  };

  useEffect(() => {
    if (isRolesSuccess) {
      setRolesOptions(dataRoles?.data?.roles.map((ele) => ({
        value: ele.id,
        label: ele.name
      })));
    }
  }, [dataRoles, isRolesSuccess]);

  useEffect(() => {
    if (isActiveUsersSuccess) {
      setActiveUsersOptions(activeUsers?.data?.getActiveUsers.map((ele) => ({
        value: ele.id,
        label: ele.name
      })));
    }
  }, [activeUsers, isActiveUsersSuccess]);

  const handleBack = () => {
    navigate(`/projects/update-project/${projectId}`);
  };

  const [openCreate, setOpenCreate] = useState(false);

  const handleClose = () => {
    setOpenCreate(false);
  };

  const handleRevokeAccess = (item) => {
    const currentUsers = data?.data?.projects?.items || [];
    
    if (currentUsers.length === 1) {
      setUserToRemove(item);
      setOpenConfirmation(true);
    } else {
      confirmRevokeAccess(item);
    }
  };

  const confirmRevokeAccess = (item) => {
    const payload = {
      projectId: projectId,
      userId: item.userId,
      roleId: item.roleId,
    };
    revokeAccess.reset();
    revokeAccess.mutate(payload, {
      onSuccess: () => {
        refetch();
        setOpenConfirmation(false);
      }
    });
  };

  const handleCloseConfirmation = () => {
    setOpenConfirmation(false);
    setUserToRemove(null);
  };

  useEffect(() => {
    if (isSuccess) {
      setDataUsers(data?.data?.projects?.items.map((item) => ({
        ...item,
        key: `${item.userId}-${item.roleId}`,
        actions: [
          {
            key: `1${item.userId}-${item.roleId}`,
            id: item.userId,
            name: "Revoke Access",
            icon: closeIcon,
            onClick: () => handleRevokeAccess(item),
          },
        ]
      })));
      
      const existingUsersData = data?.data?.projects?.items.map((item) => ({
        userId: item.userId,
        roleId: item.roleId,
        roleName: item.roleName
      }));
      setExistingUsers(existingUsersData || []);
      
      setTotalItems(data?.data?.projects?.totalItems);
    }
  }, [isSuccess, data]);

  const handleAddUser = () => {
    setOpenCreate(true);
  };

  const handleChangeSearch = (event) => {
    const { value } = event.target;
    setPages({ current: 1, pageSize: 10 });
    setSearchBy(value);
    
    setForceUpdate(!forceUpdate);
  };

  const handleChangeTable = (pagination, filters, sorter) => {
    const status = {
      "Active": 7,
      "Inactive": 8,
      "Unconfirmed": 6,
    }
    setPages(pagination);
    if (filters.statusName) {
      setStatus(status[filters.statusName]);
    } else {
      setStatus("");
    }
    setForceUpdate(!forceUpdate);
  };

  const tableColumns = [
    {
      title: "User Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: "User Last Name",
      dataIndex: "lastName",
      key: "lastName",
    },
    {
      title: "Email Address",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Contractor Company",
      dataIndex: "companiesContractors",
      key: "companiesContractors",
      render: (companiesContractors) => (
        companiesContractors?.join(", ")
      )
    },
    {
      title: "Role",
      dataIndex: "roleName",
      key: "roleName",
    },
    {
      title: "Status",
      dataIndex: "statusName",
      key: "statusName",
      render: (statusName) => (
        <WrapperStatus background={getBackgroundStatus(statusName)} color={getColorStatus(statusName)}>
          {statusName}
        </WrapperStatus>
      ),
      filterDropdown: (props) => <FiltersTable {...props} />,
      filterIcon: filtered => (
        <img src={filterIcon} style={{ color: filtered ? theme.colors.salmon : undefined }} />
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (actions) => (
        <Div gap="60px">
          {actions.map((action, i) => (
            <Link
              key={i}
              onClick={action.onClick}
              color={theme.colors.salmon}
              icon={
                <img
                  alt={action.name}
                  src={action.icon}
                  style={{ marginRight: "4px" }}
                />
              }
              styles={{
                "&:hover": {
                  background: "transparent",
                },
              }}
            >
              {action.name}
            </Link>
          ))}
        </Div>
      ),
    }
  ];

  return (
    <Container>
      <AddUserToProjectModal
        openCreate={openCreate}
        handleClose={handleClose}
        usersInfo={usersInfo}
        setUsersInfo={setUsersInfo}
        rolesOptions={rolesOptions}
        activeUsersOptions={activeUsersOptions}
        setUserRolesProject={setUserRolesProject}
        userRolesProject={userRolesProject}
        existingUsers={existingUsers}
        source="usersByProject"
        projectId={projectId}
        refetch={refetch}
      />
      <ModalConfirm
        openConfirmation={openConfirmation}
        handleCloseConfirmation={handleCloseConfirmation}
        confirmRevokeAccess={() => confirmRevokeAccess(userToRemove)}
        handleCancel={handleCloseConfirmation}
        isLoading={revokeAccess.isLoading}
      />
      <Div m="0 0 15px 0">
        <Link
          onClick={handleBack}
          color={theme.colors.grey1}
          icon={<img alt="back" src={back} style={{ marginRight: "12px" }} />}
        >
          Back
        </Link>
      </Div>
      <Div width="100%" height="55px" justify="space-between" align="center">
        <Text
          size={theme.fonts.size.h4}
          color={theme.colors.dark}
          weight={theme.fonts.weight.bold}
        >
          Project Users List
        </Text>
        <Div gap="24px">
          <Input
            placeholder="Search..."
            suffix={<img alt="search" src={searchIcon} />}
            width="324px"
            onChange={handleChangeSearch}
          />
          {hasPermission(projectId, "Assign User") && (
            <Button background={theme.colors.salmon} onClick={handleAddUser}>
              Add Users to Project
            </Button>
          )}
        </Div>
      </Div>
      <Div m="46px 0 0 0">
        <Table
          columns={tableColumns}
          data={dataUsers}
          rowClassName={(record, index) =>
            index % 2 === 0 ? "table-row-light" : "table-row-dark"
          }
          sizerLabels={["Show", "Results"]}
          pagination={{
            defaultCurrent: 1,
            ...pages,
            total: totalItems,
            showSizeChanger: true,
            pageSizeOptions: ["10", "50", "100"],
            locale: {
              items_per_page: "",
            },
            position: ["bottomCenter"],
          }}
          onChange={handleChangeTable}
          locale={{
            emptyText:
              isLoading ? (
                <Div justify="center" gap="16px">
                  {[...Array(9)].map((v, idx) => (
                    <Skeleton
                      title={false}
                      paragraph={{
                        rows: 10,
                        width: "100%",
                      }}
                      loading
                      key={idx}
                    />
                  ))}
                </Div>
              ) : (
                "No data"
              ),
          }}
        />
      </Div>
    </Container>
  )
};

